import {
  type FILTERS,
  FRAME_COLORS,
  getBleed,
  getFrameWidth,
  MATERIAL_TYPES,
  type MATTING,
  MATTING_TO_BORDER_WIDTH,
  type TILE_SIZES,
  TILESIZES_TO_INCHES,
  tileSizeToOrientation,
} from '@mixtiles/web-backend-shared'
import {
  getFrameImage,
  getFrameImageName,
} from 'core/Tile/Tile3D/components/Tile3DFrame/utils'
import { imageNameToConfig } from 'core/Tile/Tile3D/components/Tile3DFrame/consts'
import {
  BOTTOM_ROTATION_DEGREES,
  RIGHT_ROTATION_DEGREES,
} from 'components/Tile/CanvasTile/CanvasTile.consts'
import { getCanvasMaskClipPath } from 'components/Tile/CanvasTile/CanvasTile.styles'
import { getSideToTileSizeRatio } from 'utils/bleedUtils'
import { sin } from 'utils/utils'
import { getMatColorValue, normalizeMatColorToKey } from 'utils/matColorUtils'
import type { ThumbCrop } from 'stores/cartStore/CartItem'
import { getCropStyle } from './cropStyle'
import { getCssFilter } from './filters'
import * as S from './TileMini.styles'

function getFrameWidthRatio(
  materialType: MATERIAL_TYPES,
  tileSize: TILE_SIZES
): number {
  const tileInches = TILESIZES_TO_INCHES[tileSize]
  if (!tileInches) return 0
  const longerEdgeInches = Math.max(tileInches.width, tileInches.height)
  const frameWidthInches = getFrameWidth({
    materialType,
    tileSize,
    isPlastic: false,
  })
  return frameWidthInches / longerEdgeInches
}

function getMattingPaddingPercent(
  matting: MATTING,
  tileSize: TILE_SIZES
): number {
  const borders = MATTING_TO_BORDER_WIDTH[matting]
  const orientation = tileSizeToOrientation(tileSize)
  const borderWidth = borders[tileSize] ?? borders[orientation] ?? 0
  return borderWidth / 100
}

type TileMiniProps = {
  thumbUrl: string | null
  crop: ThumbCrop
  frameColor: FRAME_COLORS
  materialType: MATERIAL_TYPES
  matting: MATTING
  mattingColor: string
  filter: FILTERS
  tileSize: TILE_SIZES
  includeShadow: boolean
}

function resolveMattingColor(mattingColor: string): string {
  try {
    return getMatColorValue(normalizeMatColorToKey(mattingColor))
  } catch {
    return '#FFFFFF'
  }
}

function getFrontAspectRatio(tileSize: TILE_SIZES): number {
  const tileInches = TILESIZES_TO_INCHES[tileSize]
  return tileInches ? tileInches.width / tileInches.height : 1
}

function Photo({
  thumbUrl,
  crop,
  filter,
  multiply = false,
}: {
  thumbUrl: string | null
  crop: ThumbCrop
  filter: FILTERS
  multiply?: boolean
}) {
  if (!thumbUrl) return <S.PhotoPlaceholder />
  return (
    <S.CroppedPhoto
      src={thumbUrl}
      style={{
        ...getCropStyle(crop),
        filter: getCssFilter(filter),
        ...(multiply && { mixBlendMode: 'multiply' }),
      }}
      alt="Tile Thumbnail"
    />
  )
}

function CanvasTileMini({
  thumbUrl,
  crop,
  filter,
  tileSize,
  includeShadow,
}: Pick<
  TileMiniProps,
  'thumbUrl' | 'crop' | 'filter' | 'tileSize' | 'includeShadow'
>) {
  const { bleedWidth, bleedHeight } = getBleed({
    tileSize,
    materialType: MATERIAL_TYPES.CANVAS,
  })
  const { horizontalSideSize, verticalSideSize } = getSideToTileSizeRatio({
    tileSize,
    materialType: MATERIAL_TYPES.CANVAS,
    bleedWidth,
    bleedHeight,
  })

  const widthWithSideRatio =
    1 + horizontalSideSize * sin(RIGHT_ROTATION_DEGREES)
  const heightWithSideRatio =
    1 + verticalSideSize * sin(BOTTOM_ROTATION_DEGREES)
  const aspectRatio =
    (getFrontAspectRatio(tileSize) * widthWithSideRatio) / heightWithSideRatio

  return (
    <S.AspectBox $aspectRatio={aspectRatio}>
      {includeShadow && (
        <S.CanvasShadow
          style={{
            width: `${100 / widthWithSideRatio}%`,
            height: `${100 / heightWithSideRatio}%`,
          }}
        />
      )}
      <S.CanvasMask style={{ clipPath: getCanvasMaskClipPath({ tileSize }) }}>
        <S.Canvas3D
          $relativeRightSideSize={horizontalSideSize}
          $relativeBottomSideSize={verticalSideSize}
        />
        <S.CanvasTexture />
        <S.CanvasPhotoClip>
          <Photo thumbUrl={thumbUrl} crop={crop} filter={filter} multiply />
        </S.CanvasPhotoClip>
      </S.CanvasMask>
    </S.AspectBox>
  )
}

function FramelessTileMini({
  thumbUrl,
  crop,
  filter,
  tileSize,
  includeShadow,
}: Pick<
  TileMiniProps,
  'thumbUrl' | 'crop' | 'filter' | 'tileSize' | 'includeShadow'
>) {
  return (
    <S.AspectBox $aspectRatio={getFrontAspectRatio(tileSize)}>
      <S.FramelessDepth />
      <S.FramelessPhoto $includeShadow={includeShadow}>
        <Photo thumbUrl={thumbUrl} crop={crop} filter={filter} />
      </S.FramelessPhoto>
    </S.AspectBox>
  )
}

function FramedTileMini({
  thumbUrl,
  crop,
  frameColor,
  materialType,
  matting,
  mattingColor,
  filter,
  tileSize,
  includeShadow,
}: TileMiniProps) {
  const frameImageName = getFrameImageName(frameColor, materialType)
  const frameImageUrl = getFrameImage(frameColor, materialType)
  const borderImageSlice =
    imageNameToConfig[frameImageName]?.borderImageSlice ?? '47 44 43 47'

  const resolvedMattingColor = resolveMattingColor(mattingColor)
  const paddingPercent = getMattingPaddingPercent(matting, tileSize)
  const isWhiteFrame = frameColor === FRAME_COLORS.WHITE
  const frameWidthRatio = getFrameWidthRatio(materialType, tileSize)
  const frameLayerStyle: React.CSSProperties & {
    '--frame-width-ratio': number
    '--frame-min-width': string
  } = {
    '--frame-width-ratio': frameWidthRatio,
    '--frame-min-width': frameWidthRatio > 0 ? '1px' : '0px',
    borderImageSource: `url(${frameImageUrl})`,
    borderImageSlice,
    boxShadow: !includeShadow
      ? 'none'
      : isWhiteFrame
      ? S.WHITE_FRAME_BOX_SHADOW
      : S.FRAME_BOX_SHADOW,
  }

  return (
    <S.AspectBox $aspectRatio={getFrontAspectRatio(tileSize)}>
      <S.FrameLayer style={frameLayerStyle}>
        <S.Matting
          style={{
            padding: `${paddingPercent * 100}%`,
            backgroundColor: resolvedMattingColor,
          }}
        >
          <S.PhotoClip>
            <Photo thumbUrl={thumbUrl} crop={crop} filter={filter} />
          </S.PhotoClip>
        </S.Matting>
      </S.FrameLayer>
    </S.AspectBox>
  )
}

function TileMini(props: TileMiniProps) {
  if (props.materialType === MATERIAL_TYPES.CANVAS) {
    return <CanvasTileMini {...props} />
  }
  if (props.materialType === MATERIAL_TYPES.FRAMELESS) {
    return <FramelessTileMini {...props} />
  }
  return <FramedTileMini {...props} />
}

export default TileMini
